import { supabase } from '../integrations/supabase/client';
import { devLog } from '@/utils/devLog';

// API methods using Supabase Edge Functions
export const chatApi = {
  // Get all sessions for the user
  getSessions: async () => {
    devLog('🔄 chatApiService: Getting sessions...');
    const { data, error } = await supabase.functions.invoke('sessions', {
      method: 'GET'
    });
    
    if (error) {
      console.error('❌ chatApiService: getSessions error:', error);
      throw error;
    }
    devLog('✅ chatApiService: getSessions success:', data);
    return data;
  },
  
  // Create a new chat session
  createSession: async (title?: string) => {
    devLog('🔄 chatApiService: Creating session with title:', title);
    const { data, error } = await supabase.functions.invoke('sessions', {
      method: 'POST',
      body: { title: title || 'New Chat' }
    });
    
    if (error) {
      console.error('❌ chatApiService: createSession error:', error);
      throw error;
    }
    devLog('✅ chatApiService: createSession success:', data);
    return data;
  },
  
  // Get messages for a session
  getMessages: async (sessionId: string) => {
    devLog('🔄 chatApiService: Getting messages for session:', sessionId);
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    
    if (error) {
      console.error('❌ chatApiService: getMessages error:', error);
      throw error;
    }
    devLog('✅ chatApiService: getMessages success:', data);
    return data || [];
  },
  
  // PHASE 1: Send a message with optional skipAI flag for storing reveal messages
  sendMessage: async (sessionId: string, content: string, fileUrls?: string[], skipAI?: boolean) => {
    devLog('🔄 chatApiService: Sending message to session:', sessionId);
    devLog('🔄 chatApiService: Message content length:', content?.length || 0);
    devLog('🔄 chatApiService: File URLs:', fileUrls?.length || 0);
    devLog('🔄 chatApiService: Skip AI:', skipAI);
    
    // If skipAI is true, store message directly without calling OpenAI
    if (skipAI) {
      devLog('💾 chatApiService: Storing message directly without AI processing');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      const role = content.startsWith('Reveal target:') ? 'user' : 'assistant';
      
      const { data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          session_id: sessionId,
          user_id: user.id,
          content,
          role,
          file_urls: fileUrls || null
        })
        .select()
        .single();

      if (insertError) {
        console.error('❌ chatApiService: Error storing message:', insertError);
        throw insertError;
      }

      devLog('✅ chatApiService: Message stored successfully:', messageData?.id);
      return { data: messageData, error: null };
    }
    
    const { data, error } = await supabase.functions.invoke('chat', {
      body: {
        sessionId,
        content,
        fileUrls
      }
    });
    
    if (error) {
      console.error('❌ chatApiService: sendMessage error:', error);
      throw error;
    }
    
    devLog('✅ chatApiService: sendMessage success:', data);
    
    // ENHANCED: Check if response contains a direct confirmation
    if (data && typeof data === 'object' && data.confirmation) {
      devLog('✅ chatApiService: Direct confirmation detected:', data.confirmation);
      return data; // Return the confirmation object
    }
    
    return data;
  }
};

// Subscribe to new messages in a session using Supabase realtime
export const subscribeToMessages = (sessionId: string, onNewMessage: (message: any) => void) => {
  devLog('🔄 chatApiService: Subscribing to messages for session:', sessionId);
  
  return supabase
    .channel(`messages:session=${sessionId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `session_id=eq.${sessionId}`
      },
      (payload) => {
        devLog('🔄 chatApiService: New message received via subscription:', payload.new);
        onNewMessage(payload.new);
      }
    )
    .subscribe();
};
